import { useState, useEffect } from "react";
import { usePermissions } from "../api/auth/PermissionsContext";
import { useClientCode } from "../api/auth/ClientCodeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { sidebarItems } from "../pages/pageConfig";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

export default function Sidebar({ 
  open, 
  setOpen,
  collapsed,
  setCollapsed
}: { 
  open: boolean, 
  setOpen: (v: boolean) => void,
  collapsed: boolean,
  setCollapsed: (v: boolean) => void
}) {
  const { permissions } = usePermissions();
  const { clientCode } = useClientCode();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, setOpen]);

  // Admin-only features: visible only when clientCode === 'admin'
  const adminOnlyPermissions = new Set([
    "ACTIVITY_LOG",
    "JOB_ACTIVITY_LOG",
    "CLONE_JOB",
    "SCRIPT_TESTER",
    "PARSE_TEMPLATE",
  ]);

  // Show sidebar items with permission check; hide admin-only for non-admin clients
  const visibleItems = sidebarItems.filter(item => {
    if (clientCode !== 'admin' && adminOnlyPermissions.has(item.permission)) {
      return false;
    }
    return item.key === "CLONE_JOB" || permissions.includes(item.permission);
  });

  const palette = {
    sidebarBg: '#1a237e',
    sidebarText: '#ffffff',
    sidebarActive: '#283593',
    sidebarBorder: '#e3e8ee',
    sidebarShadow: '0 2px 12px 0 rgba(31, 38, 135, 0.10)',
    hoverBg: '#23308a',
  };

  // Sidebar content (shared for both desktop and mobile)
  const sidebarContent = (
    <Box sx={{
      width: collapsed ? 72 : 256,
      bgcolor: palette.sidebarBg,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 3,
      color: palette.sidebarText,
      borderRight: `1.5px solid ${palette.sidebarBorder}`,
      transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1)',
      overflowX: 'hidden',
      borderRadius: 0,
      // Hide scrollbar but allow scroll
      '& ::-webkit-scrollbar': { width: 0, background: 'transparent' },
      '&': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
    }}>
      {/* Header with clientCode only */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, borderBottom: 1, borderColor: 'divider', position: 'relative', cursor: 'pointer', minHeight: 56, borderRadius: 0 }} onClick={() => navigate('/dashboard')} aria-label="Go to dashboard">
        {!collapsed && <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff !important', letterSpacing: 1 }}>{clientCode}</Typography>}
        {/* Collapse/Expand Arrow (desktop only) - Centered when collapsed */}
        {isDesktop && (
          <IconButton
            sx={{ 
              position: 'absolute', 
              right: collapsed ? '50%' : 8, 
              top: '50%', 
              transform: collapsed ? 'translate(50%, -50%)' : 'translateY(-50%)', 
              color: '#fff !important', 
              borderRadius: 0,
              transition: 'right 0.2s cubic-bezier(0.4,0,0.2,1), transform 0.2s cubic-bezier(0.4,0,0.2,1)',
              '& svg': { color: '#fff !important' },
              zIndex: 10,
            }}
            onClick={e => { e.stopPropagation(); setCollapsed(!collapsed); }}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
          </IconButton>
        )}
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
      {/* Menu */}
      <List sx={{ flex: 1, overflowY: 'auto', py: 2, borderRadius: 0 }}>
        {visibleItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.key} disablePadding sx={{ display: 'block', borderRadius: 0 }}>
              <Tooltip title={collapsed ? item.label : ''} placement="right" arrow disableHoverListener={!collapsed}>
                <ListItemButton
                  selected={isActive}
                  onClick={() => {
                    navigate(item.path);
                    setOpen(false);
                    // Auto-collapse sidebar on desktop when item is clicked (same as arrow button)
                    if (isDesktop && !collapsed) {
                      setCollapsed(true);
                    }
                  }}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: 2.5,
                    borderLeft: isActive ? 4 : 0,
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    bgcolor: isActive ? palette.sidebarActive : 'transparent',
                    color: isActive ? '#fff' : palette.sidebarText,
                    borderRadius: 0,
                    boxShadow: isActive ? 2 : 0,
                    '&:hover': { bgcolor: palette.hoverBg },
                  }}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={item.label}
                >
                  <ListItemIcon sx={{ 
                    minWidth: 0, 
                    mr: collapsed ? 'auto' : 2, 
                    justifyContent: 'center', 
                    color: '#fff !important', 
                    borderRadius: 0,
                    '& svg': { color: '#fff !important' }
                  }}>
                    <Icon size={20} />
                  </ListItemIcon>
                  {!collapsed && <ListItemText 
                    primary={item.label} 
                    sx={{ 
                      opacity: collapsed ? 0 : 1, 
                      color: '#fff !important', 
                      fontWeight: 500, 
                      borderRadius: 0,
                      '& .MuiListItemText-primary': { color: '#fff !important' }
                    }} 
                  />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  // Mobile: temporary Drawer with slide animation
  if (!isDesktop) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 256,
            bgcolor: palette.sidebarBg,
            color: palette.sidebarText,
            borderRight: `1.5px solid ${palette.sidebarBorder}`,
            borderRadius: 0,
            // Hide scrollbar
            '& ::-webkit-scrollbar': { width: 0, background: 'transparent' },
            '&': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: permanent Drawer, width animates smoothly
  return (
    <Box sx={{ display: { xs: 'none', lg: 'block' }, position: 'sticky', top: 0, height: '100vh', zIndex: 1200 }}>
      <Drawer
        variant="permanent"
        open
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? 72 : 256,
            position: 'relative',
            height: '100vh',
            bgcolor: palette.sidebarBg,
            color: palette.sidebarText,
            borderRight: `1.5px solid ${palette.sidebarBorder}`,
            boxShadow: palette.sidebarShadow,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRadius: 0,
            transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1)',
            overflowX: 'hidden',
            // Hide scrollbar
            '& ::-webkit-scrollbar': { width: 0, background: 'transparent' },
            '&': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </Box>
  );
}